import type { CoopInfo, CoopMode } from '@/types'

interface CoopBadgesGame {
  coop: CoopInfo | null
  coopExperience?: { coopUrl?: string | null } | null | undefined
}

const COOP_MODE_META: Record<string, { label: string; short: string; icon: string }> = {
  online: { label: 'Online',       short: 'Online', icon: 'M2 8a6 6 0 1 1 12 0 6 6 0 0 1-12 0zM2 8h12M8 2c1.8 2 2.8 4 2.8 6S9.8 12 8 14M8 2c-1.8 2-2.8 4-2.8 6S6.2 12 8 14' },
  local:  { label: 'Local',        short: 'LAN',    icon: 'M3 10h10v3H3zM5 11.5h.01M5 4h6v4H5zM7 5.5h2M8 8v2' },
  split:  { label: 'Split-screen', short: 'Split',  icon: 'M2 3h12v10H2zM8 3v10' },
  lan:    { label: 'LAN',          short: 'LAN',    icon: 'M3 10h10v3H3zM5 11.5h.01M5 4h6v4H5zM7 5.5h2M8 8v2' },
  couch:  { label: 'Couch co-op',  short: 'Couch',  icon: 'M2 9c0-1 .8-2 2-2h8c1.2 0 2 1 2 2v3H2zM3 12v1.5M13 12v1.5M3 9V6c0-.6.4-1 1-1h8c.6 0 1 .4 1 1v3' },
}

interface CoopPlayersBadgeProps {
  game: CoopBadgesGame
  groupSize?: number | null
}

export function CoopPlayersBadge({ game, groupSize = null }: CoopPlayersBadgeProps) {
  const c = game.coop
  if (!c) return null

  const fits = groupSize !== null && groupSize >= c.min && (c.max === 0 || groupSize <= c.max)
  const tooSmall = groupSize !== null && c.max > 0 && groupSize > c.max
  const playerLabel = c.max === 0 ? `${c.min}+p` : c.min === c.max ? `${c.max}p` : `${c.min}–${c.max}p`
  const coopUrl = game.coopExperience?.coopUrl

  const title = groupSize !== null
    ? fits
      ? `Fits your group of ${groupSize}`
      : tooSmall
      ? `Group too large — supports up to ${c.max}`
      : `Supports ${playerLabel}`
    : `Co-op: ${playerLabel}`

  const inner = (
    <>
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm6 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm-6 1c-2.2 0-4 1.3-4 3v2h8v-2c0-1.7-1.8-3-4-3zm6 0a4.5 4.5 0 0 0-1.5.25A3.7 3.7 0 0 1 11 11v2h4v-2c0-1.6-1.6-3-4-3z" />
      </svg>
      <span>{playerLabel}</span>
    </>
  )

  if (coopUrl) {
    return (
      <a
        href={coopUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="cg-coop-players"
        title={title}
        data-fits={fits}
        data-toosmall={tooSmall}
      >
        {inner}
      </a>
    )
  }
  return (
    <span className="cg-coop-players" title={title} data-fits={fits} data-toosmall={tooSmall}>
      {inner}
    </span>
  )
}

interface CoopModesBadgesProps {
  coop: CoopInfo | null
  compact?: boolean
}

export function CoopModesBadges({ coop, compact = false }: CoopModesBadgesProps) {
  if (!coop) return null
  const modes: CoopMode[] = [...new Set(coop.modes.map((m) => (m === 'lan' ? 'local' : m)))]
  return (
    <>
      {modes.map((m) => {
        const meta = COOP_MODE_META[m]
        if (!meta) return null
        return (
          <span key={m} className="cg-coop-mode" title={meta.label}>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d={meta.icon} />
            </svg>
            <span>{compact ? meta.short : meta.label}</span>
          </span>
        )
      })}
    </>
  )
}

interface CoopKindBadgeProps {
  coop: CoopInfo | null
}

export function CoopKindBadge({ coop }: CoopKindBadgeProps) {
  if (!coop?.kind) return null
  const label =
    coop.kind === 'campaign' ? 'Campaign' :
    coop.kind === 'multiplayer' ? 'MP only' :
    coop.kind === 'both' ? 'Camp + MP' : null
  if (!label) return null
  const title =
    coop.kind === 'campaign' ? 'Co-op campaign supported' :
    coop.kind === 'multiplayer' ? 'Multiplayer modes only — no co-op campaign' :
    'Both campaign and multiplayer co-op'
  return (
    <span className="cg-coop-kind" title={title}>
      {label}
    </span>
  )
}

export function CoopAttribution() {
  return (
    <span className="cg-coop-attrib" title="Co-op data from Co-Optimus">
      via Co-Optimus
    </span>
  )
}
